import type { QualityGateResult } from './api/types';

/**
 * What the operator is told about a frame, and the one case where the app
 * stops to ask.
 *
 * Ported from the Base44 proof of concept's `ImageQualityChecker`, which is
 * the behaviour the field team already knows. There a frame is graded on how
 * many of its checks passed, the grade and its suggestions sit beside the
 * thumbnail, and the operator carries on. Only a frame that failed most of
 * its checks AND is either soft or badly lit holds the capture up.
 *
 * The rebuild had turned the same three measurements into a gate that any one
 * failing check tripped, routing the operator to a full screen. A correctly
 * shot photograph of a flat, low-detail surface fails the sharpness check on
 * its own, so that build stopped captures the POC had always accepted, and
 * `quality-gate.ts`'s own header forbids exactly that ("No caller in this app
 * may use this result to disable the submit action").
 *
 * The result still travels on every capture either way. Whether a failing
 * gate blocks remains a server decision (open decision B1).
 */

export type QualityGrade = 'excellent' | 'good' | 'acceptable' | 'poor';

export interface QualityAdvice {
  grade: QualityGrade;
  /** True only in the POC's narrow stop case. */
  shouldStop: boolean;
  /** One line per failed check, in the operator's words. */
  suggestions: string[];
}

const SUGGESTION: Record<string, string> = {
  resolution: 'Move closer, or fill more of the frame with the item.',
  exposure: 'Lighting is off. Move somewhere brighter, or avoid shooting into the light.',
  sharpness: 'Looks soft. Hold steady and let the camera focus, or get closer to the label.',
};

const GRADE_BY_PASS_COUNT: QualityGrade[] = ['poor', 'acceptable', 'good', 'excellent'];

export function adviseOnQuality(result: QualityGateResult): QualityAdvice {
  const passCount = result.checks.filter((check) => check.passed).length;
  const grade = GRADE_BY_PASS_COUNT[Math.min(passCount, GRADE_BY_PASS_COUNT.length - 1)];

  const failed = result.checks.filter((check) => !check.passed);
  const softOrDark = failed.some(
    (check) => check.name === 'sharpness' || check.name === 'exposure',
  );

  return {
    grade,
    shouldStop: grade === 'poor' && softOrDark,
    suggestions: failed.map(
      (check) => SUGGESTION[check.name] ?? `The ${check.name} check did not pass.`,
    ),
  };
}

const GRADE_LABEL: Record<QualityGrade, string> = {
  excellent: 'Excellent quality',
  good: 'Good quality',
  acceptable: 'Usable quality',
  poor: 'Poor quality',
};

export function gradeLabel(grade: QualityGrade): string {
  return GRADE_LABEL[grade];
}
